import React from 'react';
import {ImageViewResizeMode, ResponsiveImageView} from '@wix/devzai-utils-react';
import {OmitStrict, PartiallyOptional, Size, stringEnsurePrefix, urlPathJoin} from '@wix/devzai-utils-common';
import {
    WixImageResource, wixImageResourceGetUrlWhenExternalImage,
    wixImageResourceIsMediaManagerImageInfo,
    wixImageResourceIsResponsiveImage,
    wixImageSourceIsMediaPlatformImageInfo,
    wixMediaPlatformImageResourceGetImageUrl,
    wixResponsiveImageResourceGetScaleToCoverImageUrl,
    wixResponsiveImageResourceGetScaleToFitImageUrl
} from '@wix/devzai-common-wix';

export const WixImageView = React.memo(function WixImageView (props: WixImageView.Props) {
    return (
        <ResponsiveImageView
            resizeMode={ImageViewResizeMode.Contain}
            containerSizeChangeDebounceMs={200}
            {...props}
            isResponsiveImage={wixImageResourceIsResponsiveImage}
            resolveImageUrl={resolveImageUrl}
        />
    );
});

export namespace WixImageView {

    export type Source = WixImageResource;

    export interface Props extends OmitStrict<PartiallyOptional<ResponsiveImageView.Props<Source>, 'resizeMode' | 'containerSizeChangeDebounceMs'>, 'isResponsiveImage' | 'resolveImageUrl'> {
    }
}

function resolveImageUrl (src: WixImageResource, containerSize: Size, resizeMode: ImageViewResizeMode) {

    const externalImageUrl = wixImageResourceGetUrlWhenExternalImage(src);

    if (externalImageUrl) {
        return externalImageUrl;
    }

    if (wixImageSourceIsMediaPlatformImageInfo(src)) {
        return wixMediaPlatformImageResourceGetImageUrl(src)
    } else if (wixImageResourceIsMediaManagerImageInfo(src)) {
        const imageUrl = src.url;

        if (src.isVectorImage) {
            return `https://static.wixstatic.com${stringEnsurePrefix(urlPathJoin(src.basePath ?? 'shapes', imageUrl), '/')}`
        } else {
            const wixMediaWidth = src.width;
            const wixMediaHeight = src.height;
            const imageViewContainerWidth = containerSize.width;
            const imageViewContainerHeight = containerSize.height;

            if (imageViewContainerWidth === 0 || imageViewContainerHeight === 0) {
                // Unknown size
                return undefined;
            } else {
                switch (resizeMode) {
                    case ImageViewResizeMode.Contain:
                        return wixResponsiveImageResourceGetScaleToFitImageUrl({
                            url: imageUrl,
                            height: wixMediaHeight,
                            width: wixMediaWidth
                        }, {
                            targetWidth: imageViewContainerWidth,
                            targetHeight: imageViewContainerHeight
                        });
                    case ImageViewResizeMode.Fill:
                    case ImageViewResizeMode.Cover:
                    {
                        return wixResponsiveImageResourceGetScaleToCoverImageUrl({
                            url: imageUrl,
                            height: wixMediaHeight,
                            width: wixMediaWidth
                        }, {
                            targetWidth: imageViewContainerWidth,
                            targetHeight: imageViewContainerHeight
                        });
                    }
                    default:
                        throw new Error('Unsupported')
                }
            }
        }
    } else {
        return src;
    }
}